import Vue from 'vue';
import Router from 'vue-router';
import MainContainer from './views/MainContainer.vue';
import store from './store';
import LoginPage from './views/auth/LoginPage.vue';
import ForgotPage from './views/auth/ForgotPage.vue';
import ResetPage from './views/auth/ResetPage.vue';
import PrivacyPage from './views/auth/PrivacyPage.vue';

import AdminPanel from './views/admin/AdminPanel.vue';
import WelcomePage from './views/admin/WelcomePage.vue';

import UsersPanel from './views/admin/users/UsersPanel.vue';
import UserEdit from './views/admin/users/UserEdit.vue';

import PlaylistsPanel from './views/admin/playlists/PlaylistsPanel.vue';
import LoopPlaylistsPanel from './views/admin/playlists/LoopPlaylistsPanel.vue';
import WeeklyPlaylistsPanel from './views/admin/playlists/WeeklyPlaylistsPanel.vue';
import PlaylistEdit from './views/admin/playlists/PlaylistEdit.vue';

import DevicesPanel from './views/admin/devices/DevicesPanel.vue';
import DeviceEdit from './views/admin/devices/DeviceEdit.vue';

import GroupsPanel from './views/admin/groups/GroupsPanel.vue';
import GroupEdit from './views/admin/groups/GroupEdit.vue';

import MediaLibrary from './views/admin/media/MediaLibrary.vue';

import LogsPanel from './views/admin/logs/LogsPanel.vue';
import LogView from './views/admin/logs/LogView.vue';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      /* root level view */
      path: '/',
      name: 'main',
      redirect: '/login',
      component: MainContainer,
      children: [
        // login screen
        {
          path: 'login',
          name: 'login',
          props: true,
          component: LoginPage,
        },
        {
          name: 'forgot',
          path: 'forgot',
          component: ForgotPage,
        },
        {
          name: 'privacy',
          path: 'privacy',
          component: PrivacyPage,
        },
        {
          name: 'reset',
          path: 'reset/:token',
          props: true,
          component: ResetPage,
        },
        // all routes that need the auth
        {
          path: 'admin',
          name: 'admin',
          redirect: { name: 'welcome' },
          meta: {
            requiresAuth: true,
          },
          components:
          {
            default: AdminPanel,
          },
          children: [
            {
              path: 'welcome',
              name: 'welcome',
              component: WelcomePage,
            },
            {
              path: 'users',
              name: 'users',
              meta: {
                perms: ['users'],
              },
              component: UsersPanel,
            },
            {
              path: 'users/:id',
              name: 'showUser',
              props: true,
              meta: {
                perms: ['users'],
              },
              component: UserEdit,
            },
            {
              path: 'media/:path?',
              name: 'media',
              props: true,
              meta: {
                perms: ['media'],
              },
              component: MediaLibrary,
            },
            {
              path: 'playlists',
              name: 'playlists',
              meta: {
                perms: ['playlist-view'],
                playlistType: 'playlists',
              },
              component: PlaylistsPanel,
            },
            {
              path: 'playlists/:id',
              name: 'showPlaylist',
              meta: {
                perms: ['playlist-view'],
                playlistType: 'playlists',
              },
              props: true,
              component: PlaylistEdit,
            },
            {
              path: 'loop-playlists',
              name: 'loopPlaylists',
              meta: {
                perms: ['playlist-view'],
                playlistType: 'loop-playlists',
              },
              component: LoopPlaylistsPanel,
            },
            {
              path: 'loop-playlists/:id',
              name: 'showLoopPlaylist',
              meta: {
                perms: ['playlist-view'],
                playlistType: 'loop-playlists',
              },
              props: true,
              component: PlaylistEdit,
            },
            {
              path: 'weekly-playlists',
              name: 'weeklyPlaylists',
              meta: {
                perms: ['playlist-view'],
                playlistType: 'weekly-playlists',
              },
              component: WeeklyPlaylistsPanel,
            },
            {
              path: 'weekly-playlists/:id',
              name: 'showWeeklyPlaylist',
              meta: {
                perms: ['playlist-view'],
                playlistType: 'weekly-playlists',
              },
              props: true,
              component: PlaylistEdit,
            },
            {
              path: 'devices',
              name: 'devices',
              meta: {
                perms: ['devices'],
              },
              component: DevicesPanel,
            },
            {
              path: 'devices/:id',
              name: 'showDevice',
              props: true,
              meta: {
                perms: ['devices'],
              },
              component: DeviceEdit,
            },
            {
              path: 'groups',
              name: 'groups',
              meta: {
                perms: ['devices'],
              },
              component: GroupsPanel,
            },
            {
              path: 'groups/:id',
              name: 'showGroup',
              props: true,
              meta: {
                perms: ['devices'],
              },
              component: GroupEdit,
            },
            {
              path: 'logs',
              name: 'logs',
              meta: {
                perms: ['logs'],
              },
              component: LogsPanel,
            },
            {
              path: 'logs/:id',
              name: 'showLog',
              props: true,
              meta: {
                perms: ['logs'],
              },
              component: LogView,
            },
          ],
        },
      ],
    },
  ],
});

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(e => e.meta.requiresAuth);
  // const minimumRole = ((to.meta && to.meta.role) ? to.meta.role : false);
  const { loggedIn } = store.state.AuthStore;
  if (requiresAuth && loggedIn) {
    // proceed if logged in and roles are ok
    if (to.meta.perms) {
      store.dispatch('AuthStore/checkPerms', to.meta.perms)
        .then(() => next())
        .catch(() => next('admin'));
    } else {
      next();
    }
  } else if (requiresAuth && !loggedIn) {
    // don't proceed if not logged in but check token validity
    store.dispatch('AuthStore/checkToken').then(
      () => {
        if (to.meta.perms) {
          store.dispatch('AuthStore/checkPerms', to.meta.perms)
            .then(() => next())
            .catch(() => next('admin'));
        } else {
          next();
        }
      },
      () => {
        next('/login');
      },
    );
  } else if (!requiresAuth && loggedIn) {
    // don't allow visiting public pages if logged in
    next({ name: 'admin' });
  } else if (!requiresAuth && !loggedIn) {
    // if public but not logged in check for the saved token anyway
    store.dispatch('AuthStore/checkToken').then(
      () => {
        next('admin');
      },
      () => {
        next();
      },
    );
  }
});

export default router;


/* {
  path: '/about',
  name: 'about',
  // route level code-splitting
  // this generates a separate chunk (about.[hash].js) for this route
  // which is lazy-loaded when the route is visited.
  component: () => import(/-* webpackChunkName: "about" *-/ './views/About.vue'),
}, */
